import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isVisualizacao } from '../utils/permissoes';
import { camposFaltando, ehObrigatorio, MSG_OBRIGATORIOS } from '../utils/clienteObrigatorio';

const inpStyle = { width: '100%', padding: '8px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' };
const lbl = { color: '#6B7A8D', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' };
const th = { padding: '10px 12px', background: '#F5F7FA', color: '#9AA3AE', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' };
const td = { padding: '12px 12px', borderBottom: '0.5px solid rgba(26,58,92,0.07)', fontSize: '13px', color: '#1C2533', verticalAlign: 'middle' };

const btnPag = (active) => ({
  padding: '5px 10px', borderRadius: '6px', border: active ? 'none' : '0.5px solid rgba(26,58,92,0.15)', background: active ? '#1A6DFF' : '#fff', color: active ? '#fff' : '#6B7A8D', cursor: 'pointer', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: active ? '600' : '400', minWidth: '32px'
});

export default function Clientes() {
  const { usuario } = useAuth();
  const somenteLeitura = isVisualizacao(usuario);
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  const [cepStatus, setCepStatus] = useState('');
  const [tentouSalvar, setTentouSalvar] = useState(false); // só destaca em vermelho depois da 1ª tentativa

  // ViaCEP direto do navegador: preenche logradouro, bairro, cidade, UF e código IBGE.
  // Tudo continua editável; se o ViaCEP falhar o usuário digita à mão — nunca trava o cadastro.
  const buscaCep = async (cep) => {
    const c = cep.replace(/[^0-9]/g, '');
    setForm(f => ({ ...f, cep }));
    if (c.length !== 8) { setCepStatus(''); return; }
    setCepStatus('Buscando CEP...');
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 6000);
    try {
      const r = await fetch('https://viacep.com.br/ws/' + c + '/json/', { signal: ctrl.signal });
      const d = await r.json();
      if (d.erro) { setCepStatus('CEP não encontrado — preencha o endereço manualmente'); return; }
      setForm(f => {
        if ((f.cep || '').replace(/[^0-9]/g, '') !== c) return f; // usuário já mudou o CEP
        return {
          ...f,
          endereco: d.logradouro || f.endereco,
          bairro: d.bairro || f.bairro,
          cidade: d.localidade || f.cidade,
          estado: d.uf || f.estado,
          cod_municipio_ibge: d.ibge || f.cod_municipio_ibge,
        };
      });
      setCepStatus(d.logradouro ? 'Endereço preenchido pelo CEP — confira e informe o número' : 'CEP geral da cidade — informe logradouro e bairro');
    } catch (e) {
      setCepStatus('Não foi possível consultar o CEP — preencha o endereço manualmente');
    } finally { clearTimeout(timer); }
  };

  // Cidade/UF alteradas à mão invalidam o código IBGE vindo do CEP (código errado bloqueia a NF-e).
  const setLocal = (campo, valor) => setForm(f => ({ ...f, [campo]: valor, cod_municipio_ibge: '' }));

  useEffect(() => { api.get('/clientes').then(r => setClientes(r.data)); }, []);
  useEffect(() => { setCepStatus(''); setTentouSalvar(false); }, [modal]);

  // Reset página quando busca muda
  useEffect(() => { setPagina(1); }, [busca, porPagina]);

  const filtrados = clientes.filter(c =>
    c.nome?.toLowerCase().includes(busca.toLowerCase()) ||
    c.cpf_cnpj?.includes(busca) ||
    c.telefone?.includes(busca)
  );

  const totalPaginas = Math.ceil(filtrados.length / porPagina);
  const inicio = (pagina - 1) * porPagina;
  const paginados = filtrados.slice(inicio, inicio + porPagina);

  const faltando = camposFaltando(form);
  const vazio = (campo) => tentouSalvar && faltando.includes(campo);
  const estiloCampo = (campo) => vazio(campo) ? { ...inpStyle, border: '1px solid #C0392B', background: '#FDF2F1' } : inpStyle;
  const rotulo = (label, campo) => <label style={lbl}>{label}{ehObrigatorio(campo, form) && <span style={{ color: '#C0392B' }}> *</span>}</label>;

  const salvar = async (e) => {
    e.preventDefault();
    if (faltando.length) { setTentouSalvar(true); return; }
    setLoading(true);
    try {
      if (form.id) await api.put('/clientes/' + form.id, form);
      else await api.post('/clientes', form);
      setModal(false); setForm({});
      api.get('/clientes').then(r => setClientes(r.data));
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
    finally { setLoading(false); }
  };

  const inp = (label, campo, tipo = 'text') => (
    <div style={{ marginBottom: '12px' }}>
      {rotulo(label, campo)}
      <input type={tipo} value={form[campo] || ''} onChange={e => setForm({ ...form, [campo]: e.target.value })} style={estiloCampo(campo)} />
    </div>
  );

  // Gerar páginas para exibir (máximo 7 botões)
  const paginas = () => {
    if (totalPaginas <= 7) return Array.from({ length: totalPaginas }, (_, i) => i + 1);
    if (pagina <= 4) return [1, 2, 3, 4, 5, '...', totalPaginas];
    if (pagina >= totalPaginas - 3) return [1, '...', totalPaginas - 4, totalPaginas - 3, totalPaginas - 2, totalPaginas - 1, totalPaginas];
    return [1, '...', pagina - 1, pagina, pagina + 1, '...', totalPaginas];
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>
          Clientes <span style={{ fontSize: '14px', color: '#6B7A8D', fontWeight: '400' }}>({filtrados.length})</span>
        </div>
        {!somenteLeitura && <button onClick={() => { setForm({}); setModal(true); }}
          style={{ padding: '9px 18px', background: '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
          + Novo Cliente
        </button>}
      </div>

      {/* Busca + seletor de itens por página */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'center' }}>
        <div style={{ flex: 1, background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '10px 14px' }}>
          <input placeholder='Buscar por nome, CPF ou telefone...' value={busca} onChange={e => setBusca(e.target.value)}
            style={{ width: '100%', background: 'transparent', border: 'none', fontSize: '14px', color: '#1C2533', outline: 'none', fontFamily: "'DM Sans',system-ui,sans-serif" }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '8px 14px', whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '12px', color: '#6B7A8D' }}>Exibir</span>
          <select value={porPagina} onChange={e => setPorPagina(Number(e.target.value))}
            style={{ background: 'transparent', border: 'none', fontSize: '13px', color: '#1C2533', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', outline: 'none' }}>
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ fontSize: '12px', color: '#6B7A8D' }}>por página</span>
        </div>
      </div>

      <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>{['Nome', 'CPF/CNPJ', 'Telefone', 'Cidade', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {paginados.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>Nenhum cliente encontrado</td></tr>}
            {paginados.map(c => (
              <tr key={c.id}>
                <td style={{ ...td, fontWeight: '500' }}>{c.nome}</td>
                <td style={{ ...td, color: '#6B7A8D', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{c.cpf_cnpj}</td>
                <td style={{ ...td, color: '#6B7A8D' }}>{c.telefone}</td>
                <td style={{ ...td, color: '#6B7A8D' }}>{c.cidade}</td>
                <td style={td}>
                  {!somenteLeitura && <button onClick={() => { setForm({ ...c, data_nascimento: c.data_nascimento ? String(c.data_nascimento).slice(0, 10) : '' }); setModal(true); }}
                    style={{ padding: '4px 10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#1A3A5C', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                    Editar
                  </button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Paginação */}
        {totalPaginas > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '14px', borderTop: '0.5px solid rgba(26,58,92,0.08)' }}>
            <span style={{ fontSize: '12px', color: '#9AA3AE' }}>
              Mostrando {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length} clientes
            </span>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                style={{ ...btnPag(false), opacity: pagina === 1 ? 0.4 : 1 }}>‹</button>
              {paginas().map((p, i) =>
                p === '...' ? (
                  <span key={i} style={{ padding: '5px 4px', color: '#9AA3AE', fontSize: '13px' }}>…</span>
                ) : (
                  <button key={i} onClick={() => setPagina(p)} style={btnPag(pagina === p)}>{p}</button>
                )
              )}
              <button onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={pagina === totalPaginas}
                style={{ ...btnPag(false), opacity: pagina === totalPaginas ? 0.4 : 1 }}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '520px', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', margin: 0 }}>{form.id ? 'Editar' : 'Novo'} Cliente</h3>
              <button onClick={() => { setModal(false); setForm({}); }} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={salvar}>
              {inp('Nome Completo', 'nome')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {inp('CPF/CNPJ', 'cpf_cnpj')} {inp('RG', 'rg')}
                {inp('Data de Nascimento', 'data_nascimento', 'date')} {inp('Telefone', 'telefone')}
                {inp('Telefone 2', 'telefone2')}
              </div>
              {inp('Email', 'email', 'email')}
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0 16px', alignItems: 'end' }}>
                <div style={{ marginBottom: '12px' }}>
                  {rotulo('CEP', 'cep')}
                  <input value={form.cep || ''} onChange={e => buscaCep(e.target.value)} placeholder='00000-000' style={estiloCampo('cep')} />
                </div>
                <div style={{ marginBottom: '12px', fontSize: '12px', color: '#6B7A8D', paddingBottom: '9px' }}>{cepStatus}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '0 16px' }}>
                {inp('Logradouro', 'endereco')} {inp('Número', 'numero')}
              </div>
              {inp('Bairro', 'bairro')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 110px', gap: '0 16px' }}>
                <div style={{ marginBottom: '12px' }}>
                  {rotulo('Cidade', 'cidade')}
                  <input value={form.cidade || ''} onChange={e => setLocal('cidade', e.target.value)} style={estiloCampo('cidade')} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  {rotulo('UF', 'estado')}
                  <input value={form.estado || ''} onChange={e => setLocal('estado', e.target.value.toUpperCase())} maxLength={2} style={estiloCampo('estado')} />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={lbl}>Cód. IBGE</label>
                  <input value={form.cod_municipio_ibge || ''} onChange={e => setForm({ ...form, cod_municipio_ibge: e.target.value.replace(/\D/g, '').slice(0, 7) })} placeholder='7 dígitos' style={inpStyle} />
                </div>
              </div>
              {tentouSalvar && faltando.length > 0 && (
                <div style={{ color: '#C0392B', fontSize: '12px', fontWeight: '600', marginTop: '4px' }}>{MSG_OBRIGATORIOS}</div>
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button type='button' onClick={() => { setModal(false); setForm({}); }}
                  style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  Cancelar
                </button>
                <button type='submit' disabled={loading} aria-disabled={faltando.length > 0} title={faltando.length ? MSG_OBRIGATORIOS : ''}
                  style={{ flex: 1, padding: '10px', background: faltando.length ? '#B8C2CF' : '#1A6DFF', border: 'none', color: '#fff', borderRadius: '8px', cursor: faltando.length ? 'not-allowed' : 'pointer', fontWeight: '600', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
